import type { TradePolicyPolicyNode } from "../types/tradePolicy";
import type { ReportPick } from "./reportPickListStorage";
import type { SimulatorPickBundleCard } from "./simulatorPickBundlesStorage";

export type ResolveAtomicCardPolicyNodeDeps = {
  pickById: Map<string, ReportPick>;
  bundleById: Map<string, SimulatorPickBundleCard>;
  /**
   * Import leaf pick into 0..N policy nodes (grouping handled by caller/resolver).
   * Typically this comes from `pickImportPolicyNodes(pick, pickCatalogCtx)`.
   */
  importPickPolicyNodes: (pick: ReportPick) => TradePolicyPolicyNode[];
};

/**
 * Resolve one atomic Kanban card (leaf pick or bundle card) into a single policy node.
 *
 * - Leaf pick: imports into 0..N policy nodes; if multiple, wraps in an AND group.
 * - Bundle card: recursively resolves its atomic children and then wraps them in a group
 *   using the bundle's stored `combineOp`. This preserves the "bundle-as-atomic" UI concept
 *   at the policy-node level (i.e. the bundle becomes one policy node to its parent).
 */
export function resolveAtomicCardToPolicyNode(
  cardId: string,
  deps: ResolveAtomicCardPolicyNodeDeps,
  visited: Set<string> = new Set()
): TradePolicyPolicyNode | null {
  if (visited.has(cardId)) return null;
  visited.add(cardId);

  const pick = deps.pickById.get(cardId);
  if (pick) {
    const nodes = deps.importPickPolicyNodes(pick);
    if (nodes.length === 0) return null;
    return nodes.length === 1 ? nodes[0] : { kind: "group", op: "and", children: nodes };
  }

  const bundle = deps.bundleById.get(cardId);
  if (!bundle) return null;

  const resolvedChildren: TradePolicyPolicyNode[] = [];
  for (const childId of bundle.children) {
    const childNode = resolveAtomicCardToPolicyNode(childId, deps, visited);
    if (childNode) resolvedChildren.push(childNode);
  }

  if (resolvedChildren.length === 0) return null;
  return resolvedChildren.length === 1
    ? resolvedChildren[0]
    : { kind: "group", op: bundle.combineOp, children: resolvedChildren };
}

/** Leaf pick + policy nodes when inserting a combined Kanban card into the editor tree. */
export type BundleLeafImportUnit = {
  pick: ReportPick;
  nodes: TradePolicyPolicyNode[];
};

/**
 * Flatten a combined bundle card to leaf picks (recursing through nested bundles).
 * Used when inserting a Combined-ran card into the policy tree as root segments.
 */
export function collectBundleLeafImportUnits(
  bundleId: string,
  deps: ResolveAtomicCardPolicyNodeDeps,
  visited: Set<string> = new Set()
): BundleLeafImportUnit[] {
  if (visited.has(bundleId)) return [];
  visited.add(bundleId);

  const bundle = deps.bundleById.get(bundleId);
  if (!bundle) return [];

  const out: BundleLeafImportUnit[] = [];
  for (const childId of bundle.children) {
    const pick = deps.pickById.get(childId);
    if (pick) {
      const nodes = deps.importPickPolicyNodes(pick);
      if (nodes.length > 0) out.push({ pick, nodes });
      continue;
    }
    if (deps.bundleById.has(childId)) {
      out.push(...collectBundleLeafImportUnits(childId, deps, visited));
    }
  }
  return out;
}

